import { z } from 'zod';

// The org's first goal, picked with one tap during setup. It decides the single
// "Your First Step" recommendation on Getting Started.
export const ONBOARDING_GOALS = ['revenue', 'product', 'assistant', 'research', 'visitors'] as const;
export const OnboardingGoalSchema = z.enum(ONBOARDING_GOALS);
export type OnboardingGoal = z.infer<typeof OnboardingGoalSchema>;

export const ONBOARDING_TASK_IDS = [
  'install_tracking',
  'track_revenue',
  'invite_team',
  'connect_tool',
  'connect_ai_assistant',
  'company_profile',
] as const;
export const OnboardingTaskIdSchema = z.enum(ONBOARDING_TASK_IDS);
export type OnboardingTaskId = z.infer<typeof OnboardingTaskIdSchema>;

export type OnboardingTaskDef = {
  id: OnboardingTaskId;
  title: string;
  description: string;
};

// Every task is detected from real data; there is no manual "done". Skip covers "not for us".
export const ONBOARDING_TASKS: readonly OnboardingTaskDef[] = [
  {
    id: 'install_tracking',
    title: 'Install Tracking',
    description: 'Add one snippet to your site, or send it to whoever ships it.',
  },
  {
    id: 'track_revenue',
    title: 'Track Revenue',
    description: 'Connect the Stripe App to see which channels bring paying customers.',
  },
  {
    id: 'connect_tool',
    title: 'Connect a Tool',
    description: 'Send signals and enriched accounts to your CRM, Slack or a webhook.',
  },
  {
    id: 'connect_ai_assistant',
    title: 'Connect an AI Assistant',
    description: 'Ask about your profile and accounts in Claude, ChatGPT or Cursor.',
  },
  {
    id: 'invite_team',
    title: 'Invite Your Team',
    description: 'Add teammates so sales and marketing see the same accounts.',
  },
  {
    id: 'company_profile',
    title: 'Set Up Your Company Profile',
    description: 'Every agent uses it when it researches an account for you.',
  },
];

export const OnboardingTaskStatusSchema = z.enum(['open', 'done', 'skipped']);
export type OnboardingTaskStatus = z.infer<typeof OnboardingTaskStatusSchema>;

export type OnboardingTaskView = OnboardingTaskDef & { status: OnboardingTaskStatus };

export type OnboardingView = {
  goal: OnboardingGoal | null;
  // The free first profile build started during setup; Getting Started follows it.
  profileBuildRunId: string | null;
  wizardCompletedAt: string | null;
  autoOpenedAt: string | null;
  tasks: OnboardingTaskView[];
  // Skipped tasks count as resolved: done + skipped out of total.
  progress: { resolved: number; total: number };
};

export const OnboardingActionSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('task_skipped'), taskId: OnboardingTaskIdSchema }),
  z.object({ action: z.literal('task_reset'), taskId: OnboardingTaskIdSchema }),
  z.object({ action: z.literal('auto_opened') }),
  z.object({ action: z.literal('goal_set'), goal: OnboardingGoalSchema.nullable() }),
  z.object({ action: z.literal('profile_build_started'), runId: z.string().min(1).max(64) }),
  z.object({ action: z.literal('wizard_completed') }),
]);
export type OnboardingAction = z.infer<typeof OnboardingActionSchema>;
